/**
 * Staff capability vocabulary.
 *
 * `accounts_memberships.resources[]` gates day-to-day operational features
 * independently of the role's `.manage` permissions: an owner administers the
 * workspace, a budtender is given the floor capabilities they actually need.
 * Owners and anyone holding `members.manage` implicitly hold every capability
 * (see `jwt_has_resource` and `membership_has_resource`), so this list only
 * changes what a plain member can reach.
 *
 * Ported verbatim from ctrl-saas. The column is `text[]`, so a store can grow
 * the vocabulary without a migration — which is why this list, not an enum, is
 * the source of truth.
 *
 * The values line up with `NavItem.resource` in `navigation.ts`: ticking
 * `catalog` here is what makes Products, Collections and Taxonomy appear in
 * that member's sidebar.
 */
export interface StaffResource {
  value: string;
  label: string;
  description: string;
}

export const STAFF_RESOURCES: StaffResource[] = [
  {
    value: 'checkin',
    label: 'Check-in',
    description: 'Scan IDs and check customers in',
  },
  {
    value: 'fulfillment',
    label: 'Fulfillment',
    description: 'Package orders and record hand-offs',
  },
  {
    value: 'delivery',
    label: 'Delivery',
    description: 'Be assigned delivery runs',
  },
  {
    value: 'orders',
    label: 'Orders',
    description: 'View and work the orders board',
  },
  {
    value: 'catalog',
    label: 'Catalog',
    description: 'Edit products and menus',
  },
  {
    value: 'inventory',
    label: 'Inventory',
    description: 'Adjust stock and receive product',
  },
  {
    value: 'customers',
    label: 'Customers',
    description: 'View and edit customer records',
  },
  {
    value: 'reports',
    label: 'Reports',
    description: 'See sales and analytics',
  },
  {
    value: 'marketing',
    label: 'Marketing',
    description: 'Send campaigns and manage promos',
  },
  {
    value: 'support',
    label: 'Support chat',
    description: 'Answer live chat from the storefront',
  },
  /*
   * The next two were always in the database's vocabulary -- the read gates in
   * 20260828020000 name `discounts` and `payments` for customers, discounts and
   * payment_transactions -- but never in this list. The sidebar gates
   * /discounts and /gift-cards on them, so no member could be granted either
   * and the two items were invisible to everyone but owners.
   */
  {
    value: 'discounts',
    label: 'Discounts',
    description: 'Create promo codes and automatic discounts',
  },
  {
    value: 'payments',
    label: 'Payments',
    description: 'Issue gift cards, record refunds and reconcile takings',
  },
];

const RESOURCE_LABELS = new Map(
  STAFF_RESOURCES.map((resource) => [resource.value, resource.label]),
);

/** Falls back to the raw value: a store-defined capability still needs a name. */
export function resourceLabel(value: string): string {
  return RESOURCE_LABELS.get(value) ?? value;
}
